import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { startExpand, startCollapse, reassertExpand, cancelExpand } from "../lib/flipExpand.js";
import { readUrlParam, writeUrlParam } from "../lib/urlParams.js";

const STACKED_LAYOUT_BREAKPOINT = 960;
const MIN_COLUMN_WIDTH = 320;

/**
 * Compute the expand destination rect: full container width (minus section padding),
 * starting below the column header.
 */
const EXPAND_GAP_TOP = 12;
const EXPAND_GAP_BOTTOM = 12;

// `full` (overview pivot) expands the target over the column header too —
// starting at the container top instead of below the header — so the
// full-screen surface grows up and over the tab strip with no layout shift,
// rather than the header being removed from flow.
function computeExpandRect(container, colEl, full = false) {
  const containerRect = container.getBoundingClientRect();
  const section = colEl.querySelector("section");
  const contentDiv = colEl.querySelector("section > div:last-child");
  const contentTop = full
    ? containerRect.top
    : (contentDiv ? contentDiv.getBoundingClientRect().top : containerRect.top);
  const style = section ? getComputedStyle(section) : null;
  const padLeft = style ? parseFloat(style.paddingLeft) || 0 : 0;
  const padRight = style ? parseFloat(style.paddingRight) || 0 : 0;
  const top = contentTop + EXPAND_GAP_TOP;
  return {
    top,
    left: containerRect.left + padLeft,
    width: containerRect.width - padLeft - padRight,
    height: Math.max(0, containerRect.bottom - top - EXPAND_GAP_BOTTOM),
  };
}

export default function ContentShell({ columns, className = "" }) {
  const containerRef = useRef(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  // Full-width column expansion state ("xcol=<id>" in the URL) round-
  // trips through search params so reload restores whatever column
  // the operator had stretched to fill the shell. Skipped when the
  // referenced column doesn't exist on the current page. URL access
  // intentionally side-steps useSearchParams so write-back doesn't
  // cascade re-renders through the whole tree mid-animation — see
  // writeUrlParam above.
  const initialExpandedFromUrl = readUrlParam("xcol");
  const [expandedColumn, setExpandedColumn] = useState(() => {
    if (!initialExpandedFromUrl) return null;
    return columnsRef.current.some((c) => c.id === initialExpandedFromUrl)
      ? initialExpandedFromUrl
      : null;
  });
  const [animating, setAnimating] = useState(false);
  const [isStacked, setIsStacked] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${STACKED_LAYOUT_BREAKPOINT}px)`).matches
      : false,
  );

  // Imperative position overrides — applied after every React render
  const overridesRef = useRef(null);

  // Pending FLIP action
  const flipRef = useRef(null);

  // Column styles rendered while `animating` — captured at animation start
  // so mid-animation re-renders can't write conflicting geometry.
  const frozenStylesRef = useRef(null);

  // Expand state for the lifted-target mode (from flipExpand utility)
  const expandStateRef = useRef(null);

  // Mirror expandedColumn → ?xcol=. Bare history.replaceState (no
  // react-router subscription) so the URL update doesn't cascade a
  // re-render across every useSearchParams consumer mid-animation.
  // Still gated on !animating so we don't fight with FLIP's inline
  // styling on the very first frame after a toggle.
  useEffect(() => {
    if (animating) return;
    writeUrlParam("xcol", expandedColumn || null);
  }, [expandedColumn, animating]);

  // On mount with ?xcol=<id> in the URL, replay whichever expand flow
  // handleToggleExpand would have used — but with no animation, so the
  // page paints directly into the final state.
  //   - column-only: column fills container width.
  //   - target-level: column fills width AND the `[data-expand-target]`
  //     inside is lifted via flipExpand into the same fixed-position
  //     fullscreen rect the runtime FLIP ends at, surrounding
  //     `[data-expand-collapse]` siblings hidden.
  //
  // The restoredRef guard keeps StrictMode's intentional double-invoke
  // from re-running startExpand against an already-lifted target (the
  // second pass would create a duplicate placeholder/backdrop and
  // reparent the now-fixed element again).
  const restoredRef = useRef(false);
  useLayoutEffect(() => {
    if (!initialExpandedFromUrl || restoredRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    const colEl = container.querySelector(`[data-column="${initialExpandedFromUrl}"]`);
    if (!colEl) return;
    restoredRef.current = true;

    const w = container.offsetWidth;
    colEl.style.left = "0px";
    colEl.style.width = `${w}px`;
    colEl.style.right = "auto";
    colEl.style.zIndex = "5";
    colEl.style.transition = "none";

    const target = colEl.querySelector("[data-expand-target]");
    if (target) {
      // Outer collapsibles are also collapsed by the column-render
      // useLayoutEffect (lines further down) when isExpanded is true,
      // but we set them here too so the values are in place before
      // startExpand measures the target.
      const outer = [...colEl.querySelectorAll("[data-expand-collapse]")]
        .filter((el) => !target.contains(el));
      outer.forEach((el) => {
        el.style.position = "absolute";
        el.style.visibility = "hidden";
        el.style.height = "0px";
        el.style.overflow = "hidden";
        el.style.opacity = "0";
        el.style.transition = "none";
      });

      const scrollViewport = colEl.querySelector("[data-overlayscrollbars-viewport]") || colEl.querySelector(".os-viewport");
      // ?pin marks the overview-pivot (full / cover-header) expand, so a
      // reload restores it covering the tab strip rather than below it.
      const expandRect = computeExpandRect(container, colEl, readUrlParam("pin") != null);
      const state = startExpand(target, container, expandRect, {
        duration: 0,
        scrollViewport,
        sourceRect: target.getBoundingClientRect(),
        onComplete: () => {
          overridesRef.current = {
            [initialExpandedFromUrl]: { left: "0px", right: "0px", width: "", zIndex: 5, transition: "none" },
          };
        },
      });
      state.columnId = initialExpandedFromUrl;
      expandStateRef.current = state;
    } else {
      overridesRef.current = {
        [initialExpandedFromUrl]: { left: "0px", right: "0px", width: "", zIndex: 5, transition: "none" },
      };
    }

    const shell = container.closest("[data-shell]");
    if (shell) {
      shell.dataset.columnExpanded = initialExpandedFromUrl;
      shell.dispatchEvent(new CustomEvent("columnexpandchange", { bubbles: true, detail: initialExpandedFromUrl }));
    }
  }, []); // mount only

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia(`(max-width: ${STACKED_LAYOUT_BREAKPOINT}px)`);
    const listener = (e) => setIsStacked(e.matches);
    setIsStacked(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);

  // Escape key
  useEffect(() => {
    if (!expandedColumn) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // A modal over the page (the chat console, a trace's canvas, a dialog)
      // takes its own Esc; the column under it stays as it is.
      if (document.querySelector('[aria-modal="true"]')) return;
      handleToggleExpand(expandedColumn);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  // External expand/collapse command — lets the overview pivot (App.jsx)
  // drive a column full-screen through the exact same animated path as the
  // in-surface expand button, with no ref and no remount (the remount was
  // what made the previous pivot feel clunky). detail:
  //   { columnId, mode: "expand" | "collapse" | "toggle", full?: boolean }
  // `full` expands the target over the column header too (overview pivot).
  // Guarded to this shell's own columns so sibling ContentShells ignore it.
  useEffect(() => {
    const onCmd = (e) => {
      const { columnId, mode, full } = e.detail || {};
      const id = columnId || expandedColumn;
      if (!id || !columnsRef.current.some((c) => c.id === id)) return;
      if (mode === "cancel") {
        // Instant, non-animated exit — dispatched by SplitPage before its
        // detail-column FLIP so the two systems never animate the same
        // column's inline styles at once. Scoped to the NAMED column: a
        // takeover on a different column (or in a sibling shell listening
        // on window) must not be torn down as a side effect.
        if (expandedColumn && expandedColumn === id && expandStateRef.current?.columnId === id) {
          cancelActiveExpand();
        }
      } else if (mode === "collapse") {
        // Only collapse when the TARGETED column is the expanded one. A
        // "collapse detail" command (e.g. the chat column auto-collapsing
        // on mount when chat is the default panel) must not tear down an
        // unrelated focus expand — like a restored ?xcol=focus.
        if (expandedColumn && expandedColumn === id) handleToggleExpand(expandedColumn, full);
      } else if (mode === "expand") {
        if (expandedColumn !== id) handleToggleExpand(id, full);
      } else {
        handleToggleExpand(id, full);
      }
    };
    window.addEventListener("origin-column-expand", onCmd);
    return () => window.removeEventListener("origin-column-expand", onCmd);
  }, [expandedColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ResizeObserver: re-assert expanded column position when container resizes
  // (e.g., sidebar collapse/expand changes the available width)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !expandedColumn || animating) return undefined;
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      const w = container.offsetWidth;
      // Column-level expand
      if (overridesRef.current && !expandStateRef.current) {
        const el = container.querySelector(`[data-column="${expandedColumn}"]`);
        if (el) {
          el.style.left = "0px";
          el.style.width = `${w}px`;
          el.style.transition = "none";
        }
      }
      // Target-level expand
      const es = expandStateRef.current;
      if (es && !es.collapsing) {
        const colEl = container.querySelector(`[data-column="${es.columnId}"]`);
        if (colEl) {
          reassertExpand(es, computeExpandRect(container, colEl, readUrlParam("pin") != null));
          colEl.style.width = `${w}px`;
          colEl.style.transition = "none";
        }
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [expandedColumn, animating]);

  // Instant exit from a lifted-target takeover, for the two cases the
  // animated collapse can't serve: the owning view unmounted (the target
  // is an orphaned ghost — see the reassert effect below), or a competing
  // column animation (SplitPage's detail FLIP) needs the column back NOW.
  function cancelActiveExpand() {
    const es = expandStateRef.current;
    const container = containerRef.current;
    if (!es) return;
    // An in-flight collapse is already exiting the takeover and owns the
    // restore — cancelling under it would re-add nodes its completion
    // callback is about to move.
    if (es.collapsing) return;
    const colEl = container?.querySelector(`[data-column="${es.columnId}"]`);
    cancelExpand(es, colEl);
    if (colEl) {
      const cols = columnsRef.current;
      const ci = cols.findIndex((c) => c.id === es.columnId);
      if (ci >= 0) {
        const cs = buildColumnStyle(cols[ci], ci);
        colEl.style.left = cs.left || "";
        colEl.style.right = cs.right || "";
        colEl.style.width = cs.width || "";
        colEl.style.zIndex = String(cols[ci].zIndex || 0);
        colEl.style.transition = "none";
      }
    }
    expandStateRef.current = null;
    overridesRef.current = null;
    setAnimating(false);
    setExpandedColumn(null);
    const shell = container?.closest("[data-shell]");
    if (shell) {
      delete shell.dataset.columnExpanded;
      shell.dispatchEvent(new CustomEvent("columnexpandchange", { bubbles: true, detail: null }));
    }
  }

  function computePositions(containerWidth) {
    const cols = columnsRef.current;
    let fixedSum = 0;
    for (const c of cols) { if (!c.flex && c.width) fixedSum += c.width; }
    const flexWidth = Math.max(MIN_COLUMN_WIDTH, containerWidth - fixedSum);
    const positions = [];
    let left = 0;
    for (const c of cols) {
      const w = c.flex ? flexWidth : (c.width ?? 360);
      positions.push({ id: c.id, left, width: w });
      left += w;
    }
    return positions;
  }

  // Re-assert expanded position after every React render (before paint)
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Column-level expanded: reassert at current container width (responsive to resizes)
    if (overridesRef.current && !expandStateRef.current) {
      for (const id of Object.keys(overridesRef.current)) {
        const el = container.querySelector(`[data-column="${id}"]`);
        if (!el) continue;
        el.style.left = "0px";
        el.style.right = "0px";
        el.style.width = `${container.offsetWidth}px`;
        el.style.zIndex = "5";
        el.style.transition = "none";
      }
    }

    // Lifted-target: re-assert via flipExpand utility
    const es = expandStateRef.current;
    if (es && !es.collapsing) {
      // The target was REPARENTED out of the column, so when its owning
      // view unmounts (lens switch under ?xcol) React removes the
      // column's body — including the placeholder — but not the target:
      // a fixed-position ghost with dead handlers over the new view,
      // and a column still carrying the takeover overrides. Detect the
      // placeholder's disconnection and tear the whole state down.
      if (es.placeholderEl && !es.placeholderEl.isConnected) {
        cancelActiveExpand();
        return;
      }
      const colEl = container.querySelector(`[data-column="${es.columnId}"]`);
      if (colEl) {
        reassertExpand(es, computeExpandRect(container, colEl, readUrlParam("pin") != null));
      }
    }
  });

  // Snap inner headers and collapsible elements when settled
  useLayoutEffect(() => {
    if (animating) return;
    const container = containerRef.current;
    if (!container) return;
    for (const col of columns) {
      const colEl = container.querySelector(`[data-column="${col.id}"]`);
      if (!colEl) continue;
      const isExpanded = expandedColumn === col.id;

      // Inner headers (column B context header)
      const hdr = colEl.querySelector("[data-context-header], [data-expand-inner]");
      if (hdr) {
        if (isExpanded) {
          hdr.style.height = "0px";
          hdr.style.overflow = "hidden";
          hdr.style.opacity = "0";
          hdr.style.transition = "none";
        } else {
          hdr.style.cssText = "";
        }
      }

      // Collapsible elements — skip those inside a lifted target
      const targetEl = expandStateRef.current?.targetEl;
      for (const el of colEl.querySelectorAll("[data-expand-collapse]")) {
        if (targetEl && targetEl.contains(el)) continue;
        if (isExpanded) {
          el.style.position = "absolute";
          el.style.visibility = "hidden";
          el.style.height = "0px";
          el.style.overflow = "hidden";
          el.style.opacity = "0";
          el.style.transition = "none";
        } else {
          el.style.position = "";
          el.style.visibility = "";
          el.style.height = "";
          el.style.overflow = "";
          el.style.opacity = "";
          el.style.transition = "";
        }
      }
    }
  }, [expandedColumn, animating, columns]);

  // FLIP action after React commit
  useLayoutEffect(() => {
    const flip = flipRef.current;
    if (!flip) return;
    flipRef.current = null;

    const container = containerRef.current;
    if (!container) return;
    const cols = columnsRef.current;
    const easing = "cubic-bezier(0.4, 0, 0.2, 1)";
    const colEl = container.querySelector(`[data-column="${flip.columnId}"]`);
    if (!colEl) return;

    // ─── EXPAND-TARGET (via flipExpand utility) ─────────────────────
    if (flip.type === "expand-target") {
      const target = colEl.querySelector("[data-expand-target]");
      if (!target) return;

      const scrollViewport = colEl.querySelector("[data-overlayscrollbars-viewport]") || colEl.querySelector(".os-viewport");
      const flipEasing = "cubic-bezier(0.4, 0, 0.2, 1)";
      const flipDuration = 450;

      // Set column to explicit current position (so we can animate it)
      const colRect = colEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      colEl.style.left = `${colRect.left - containerRect.left}px`;
      colEl.style.width = `${colRect.width}px`;
      colEl.style.right = "auto";
      colEl.style.zIndex = "5";
      colEl.style.transition = "none";

      // Collapse outer collapsibles (hero, log tabs) before the target
      // is reparented — prevents a layout flash while they're still in flow.
      const outer = [...colEl.querySelectorAll("[data-expand-collapse]")]
        .filter((el) => !target.contains(el));
      outer.forEach((el) => {
        el.style.height = `${el.offsetHeight}px`;
        el.style.overflow = "hidden";
        el.style.transition = "none";
      });
      void colEl.offsetHeight;
      outer.forEach((el) => {
        el.style.transition = `height ${flipDuration * 0.5}ms ${flipEasing}, opacity ${flipDuration * 0.35}ms ease-out`;
        el.style.height = "0px";
        el.style.opacity = "0";
      });

      // Start the target FLIP (pass sourceRect captured before React re-render
      // so the starting position reflects the pre-expand layout)
      const state = startExpand(target, container, flip.expandRect, {
        duration: flipDuration,
        easing: flipEasing,
        scrollViewport,
        sourceRect: flip.sourceRect,
        onComplete: () => {
          // Lock column at full width via overrides
          overridesRef.current = {
            [flip.columnId]: { left: "0px", right: "0px", width: "", zIndex: 5, transition: "none" },
          };
          colEl.style.transition = "none";
          setAnimating(false);
        },
      });

      // Animate column to full width simultaneously
      colEl.style.transition = `left ${flipDuration}ms ${flipEasing}, width ${flipDuration}ms ${flipEasing}`;
      colEl.style.left = "0px";
      colEl.style.width = `${flip.containerWidth}px`;

      state.columnId = flip.columnId;
      expandStateRef.current = state;
    }

    // ─── COLLAPSE-TARGET (via flipExpand utility) ───────────────────
    else if (flip.type === "collapse-target") {
      const es = expandStateRef.current;
      if (!es) return;

      const flipEasing = "cubic-bezier(0.4, 0, 0.2, 1)";
      const flipDuration = 450;
      const positions = computePositions(flip.containerWidth);
      const colPos = positions.find((p) => p.id === flip.columnId);

      startCollapse(es, container, colEl, {
        duration: flipDuration,
        easing: flipEasing,

        // Before measuring destination: reset column to original width
        onBeforeMeasure: () => {
          overridesRef.current = null;
          colEl.style.left = `${colPos?.left || 0}px`;
          colEl.style.width = `${colPos?.width || 360}px`;
          colEl.style.right = "auto";
          colEl.style.transition = "none";
        },

        // After FLIP setup: set column back to full width, then animate shrink
        onBeforeAnimate: () => {
          colEl.style.left = "0px";
          colEl.style.width = `${flip.containerWidth}px`;
          colEl.style.transition = "none";
          void colEl.offsetHeight;
          colEl.style.transition = `left ${flipDuration}ms ${flipEasing}, width ${flipDuration}ms ${flipEasing}`;
          colEl.style.left = `${colPos?.left || 0}px`;
          colEl.style.width = `${colPos?.width || 360}px`;
        },

        onComplete: () => {
          // Restore column to CSS-driven positioning
          const colIndex = cols.findIndex((c) => c.id === flip.columnId);
          if (colIndex >= 0) {
            const cs = buildColumnStyle(cols[colIndex], colIndex);
            colEl.style.left = cs.left || "";
            colEl.style.right = cs.right || "";
            colEl.style.width = cs.width || "";
            colEl.style.zIndex = String(cols[colIndex].zIndex || 0);
            colEl.style.transition = "none";
          }
          expandStateRef.current = null;
          setAnimating(false);
        },
      });
    }

    // ─── EXPAND (column-level, no target — used by detail panel) ────
    else if (flip.type === "expand") {
      const hdr = colEl.querySelector("[data-context-header], [data-expand-inner]");
      const rect = colEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const startLeft = rect.left - containerRect.left;
      const startWidth = rect.width;

      if (hdr) {
        hdr.style.height = `${flip.innerHdrH}px`;
        hdr.style.overflow = "hidden";
        hdr.style.opacity = "1";
        hdr.style.transition = "none";
      }
      colEl.style.left = `${startLeft}px`;
      colEl.style.width = `${startWidth}px`;
      colEl.style.right = "auto";
      colEl.style.zIndex = "5";
      colEl.style.transition = "none";

      const collapsibles = [...colEl.querySelectorAll("[data-expand-collapse]")];
      collapsibles.forEach((el) => {
        const h = el.offsetHeight;
        el.style.height = `${h}px`;
        el.style.overflow = "hidden";
        el.style.transition = "none";
      });

      const scrollViewport = colEl.querySelector("[data-overlayscrollbars-viewport]") || colEl.querySelector(".os-viewport");
      if (scrollViewport) scrollViewport.scrollTop = 0;

      void colEl.offsetHeight;

      colEl.style.transition = `left 450ms ${easing}, width 450ms ${easing}`;
      colEl.style.left = "0px";
      colEl.style.width = `${flip.containerWidth}px`;

      if (hdr) {
        hdr.style.transition = `height 650ms ${easing}, opacity 200ms ease-out`;
        hdr.style.height = "0px";
        hdr.style.opacity = "0";
      }

      collapsibles.forEach((el) => {
        el.style.transition = `height 450ms ${easing}, opacity 200ms ease-out`;
        el.style.height = "0px";
        el.style.opacity = "0";
      });

      const cleanup = () => {
        colEl.removeEventListener("transitionend", onEnd);
        overridesRef.current = {
          [flip.columnId]: { left: "0px", right: "0px", width: "", zIndex: 5, transition: "none" },
        };
        for (let ci = 0; ci < cols.length; ci++) {
          const c = cols[ci];
          const el = container.querySelector(`[data-column="${c.id}"]`);
          if (!el) continue;
          if (c.id === flip.columnId) {
            el.style.transition = "none";
          } else {
            const cs = buildColumnStyle(c, ci);
            el.style.left = cs.left || "";
            el.style.right = cs.right || "";
            el.style.width = cs.width || "";
            el.style.zIndex = String(c.zIndex || 0);
            el.style.transition = "none";
          }
        }
        setAnimating(false);
      };
      const onEnd = (e) => { if (e.target === colEl) cleanup(); };
      colEl.addEventListener("transitionend", onEnd);
      setTimeout(cleanup, 550);
    }

    // ─── COLLAPSE (column-level, no target) ─────────────────────────
    else if (flip.type === "collapse") {
      const hdr = colEl.querySelector("[data-context-header], [data-expand-inner]");
      const currentWidth = colEl.offsetWidth || flip.containerWidth;
      colEl.style.left = "0px";
      colEl.style.right = "auto";
      colEl.style.width = `${currentWidth}px`;
      colEl.style.zIndex = "5";
      colEl.style.transition = "none";

      if (hdr) {
        hdr.style.height = "0px";
        hdr.style.overflow = "hidden";
        hdr.style.opacity = "0";
        hdr.style.transition = "none";
      }

      const collapsibles = [...colEl.querySelectorAll("[data-expand-collapse]")];
      collapsibles.forEach((el) => {
        el.style.position = "";
        el.style.visibility = "";
        el.style.height = "0px";
        el.style.overflow = "hidden";
        el.style.opacity = "0";
        el.style.transition = "none";
      });

      for (const c of cols) {
        if (c.id === flip.columnId) continue;
        const sec = container.querySelector(`[data-column="${c.id}"] section`);
        if (sec) { sec.style.opacity = "0"; sec.style.transition = "none"; }
      }

      void colEl.offsetHeight;

      requestAnimationFrame(() => {
        const positions = computePositions(flip.containerWidth);
        const pos = positions.find((p) => p.id === flip.columnId);

        colEl.style.transition = `left 450ms ${easing}, width 450ms ${easing}`;
        colEl.style.left = `${pos?.left || 0}px`;
        colEl.style.width = `${pos?.width || 400}px`;

        for (const c of cols) {
          if (c.id === flip.columnId) continue;
          const sec = container.querySelector(`[data-column="${c.id}"] section`);
          if (sec) { sec.style.transition = "opacity 350ms cubic-bezier(0.4, 0, 0.2, 1)"; sec.style.opacity = "1"; }
        }

        for (const el of collapsibles) {
          const targetH = el.scrollHeight;
          el.style.transition = `height 450ms ${easing}, opacity 300ms ease-in 200ms`;
          el.style.height = `${targetH}px`;
          el.style.opacity = "1";
        }

        if (hdr) {
          const targetH = hdr.scrollHeight;
          hdr.style.transition = `height 450ms ${easing}, opacity 250ms ease-in 200ms`;
          hdr.style.height = `${targetH}px`;
          hdr.style.opacity = "1";
        }

        const cleanup = () => {
          colEl.removeEventListener("transitionend", onEnd);
          overridesRef.current = null;
          for (let ci = 0; ci < cols.length; ci++) {
            const c = cols[ci];
            const el = container.querySelector(`[data-column="${c.id}"]`);
            if (!el) continue;
            const cs = buildColumnStyle(c, ci);
            el.style.left = cs.left || "";
            el.style.right = cs.right || "";
            el.style.width = cs.width || "";
            el.style.zIndex = String(c.zIndex || 0);
            el.style.transition = "none";
            const sec = el.querySelector("section");
            if (sec) {
              sec.style.opacity = "1";
              sec.style.transition = "opacity 350ms cubic-bezier(0.4, 0, 0.2, 1)";
              sec.style.pointerEvents = "";
            }
          }
          if (hdr) hdr.style.cssText = "";
          for (const el of collapsibles) {
            el.style.height = "";
            el.style.overflow = "";
            el.style.opacity = "";
            el.style.transition = "";
          }
          setAnimating(false);
        };
        const onEnd = (e) => { if (e.target === colEl) cleanup(); };
        colEl.addEventListener("transitionend", onEnd);
        setTimeout(cleanup, 550);
      });
    }
  }, [expandedColumn, animating]);

  const handleToggleExpand = useCallback((columnId, full = false) => {
    if (isStacked) {
      setExpandedColumn((v) => (v === columnId ? null : columnId));
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    const containerWidth = container.offsetWidth;
    const colEl = container.querySelector(`[data-column="${columnId}"]`);
    if (!colEl) return;

    const target = colEl.querySelector("[data-expand-target]");

    // Freeze the React-rendered column styles for the animation's duration.
    // A navigation or resolving query can re-render mid-FLIP with different
    // widths, and React writing a changed inline width onto a column whose
    // geometry the FLIP owns snaps it visibly. Rendering the captured styles
    // until `animating` clears means those renders diff against identical
    // values and leave the DOM alone.
    frozenStylesRef.current = Object.fromEntries(
      columnsRef.current.map((c, i) => [c.id, buildColumnStyle(c, i)]),
    );

    if (expandedColumn !== columnId) {
      // EXPANDING
      if (target) {
        const expandRect = computeExpandRect(container, colEl, full);
        const sourceRect = target.getBoundingClientRect();
        flipRef.current = { type: "expand-target", columnId, expandRect, sourceRect, containerWidth, full };
      } else {
        const innerHdr = colEl.querySelector("[data-context-header], [data-expand-inner]");
        flipRef.current = { type: "expand", columnId, containerWidth, innerHdrH: innerHdr?.offsetHeight || 0 };
      }
      overridesRef.current = null;
      setExpandedColumn(columnId);
      setAnimating(true);
      const shell = containerRef.current?.closest("[data-shell]");
      if (shell) {
        shell.dataset.columnExpanded = columnId;
        shell.dispatchEvent(new CustomEvent("columnexpandchange", { bubbles: true, detail: columnId }));
      }
    } else {
      // COLLAPSING
      // Release the column-level pin NOW, not at completion. The re-assert
      // pass runs on every render and re-applies `overridesRef` with
      // transition:none — left set, any render during the ~half-second of
      // collapse yanks the column back to full width and the animation
      // "completes" as a frozen pin plus an end-state snap.
      overridesRef.current = null;
      // Drop the URL param up front as well: the settled mirror writes the
      // same null after the animation, but anything that remounts the shell
      // mid-collapse (scope change, error boundary) would otherwise replay
      // a full-screen takeover nobody is looking at.
      writeUrlParam("xcol", null);
      if (expandStateRef.current) {
        flipRef.current = { type: "collapse-target", columnId, containerWidth };
      } else {
        flipRef.current = { type: "collapse", columnId, containerWidth };
      }
      setExpandedColumn(null);
      setAnimating(true);
      const shell = containerRef.current?.closest("[data-shell]");
      if (shell) {
        delete shell.dataset.columnExpanded;
        shell.dispatchEvent(new CustomEvent("columnexpandchange", { bubbles: true, detail: null }));
      }
    }
  }, [expandedColumn, isStacked]);

  // Build CSS-relative positioning for each column.
  function buildColumnStyle(col, colIndex) {
    const cols = columnsRef.current;
    let rightOffset = 0;
    for (let j = colIndex + 1; j < cols.length; j++) {
      if (!cols[j].flex) rightOffset += cols[j].width ?? 360;
    }
    let leftOffset = 0;
    for (let j = 0; j < colIndex; j++) {
      if (!cols[j].flex) leftOffset += cols[j].width ?? 360;
    }

    if (col.flex) {
      return { left: `${leftOffset}px`, right: `${rightOffset}px` };
    }
    return { right: `${rightOffset}px`, width: `${col.width ?? 360}px` };
  }

  return (
    <main
      ref={containerRef}
      className={["relative h-full min-h-0 overflow-hidden", className].filter(Boolean).join(" ")}
    >
      {columns.map((col, i) => {
        const isExpanded = expandedColumn === col.id;
        const isFaded = expandedColumn != null && expandedColumn !== col.id;
        const cs = (animating && frozenStylesRef.current?.[col.id]) || buildColumnStyle(col, i);

        return (
          <div
            key={col.id}
            data-column={col.id}
            className="absolute top-0 bottom-0 min-h-0 min-w-0"
            style={{
              ...cs,
              zIndex: col.zIndex || 0,
              transition: "none",
            }}
          >
            <section
              className="flex flex-col h-full min-h-0 min-w-0 bg-[color:var(--bg)] px-[var(--main-inset)]"
              style={{
                opacity: isFaded ? 0 : 1,
                transition: "opacity 350ms cubic-bezier(0.4, 0, 0.2, 1)",
                pointerEvents: isFaded ? "none" : undefined,
              }}
            >
              <header className={`shrink-0 overflow-hidden ${col.headerClassName || ""}`}>
                {col.header}
              </header>
              <div className="flex-1 min-h-0 min-w-0">
                {col.content({
                  expanded: isExpanded,
                  animating,
                  onToggleExpand: () => handleToggleExpand(col.id),
                })}
              </div>
            </section>
          </div>
        );
      })}
    </main>
  );
}

export { STACKED_LAYOUT_BREAKPOINT, MIN_COLUMN_WIDTH };
